import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { Spinner, Alert, Button, Form, Modal } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Check, X, Clock, Send, AlertTriangle, Search, RefreshCw,
  GripVertical, Calendar, ChevronDown, ChevronUp, Briefcase,
  LayoutGrid, List as ListIcon,
  User, Users, Building2,
} from 'lucide-react';
import PageTitle from '../../../layouts/PageTitle';
import * as api from '../../../../services/api/timeReportingApi';
import { useCapabilities } from '../../../../context/CapabilityContext';
import { format, eachDayOfInterval, isWeekend } from 'date-fns';

// ─── STATUS TAXONOMY ───
// Single source of truth — powers kanban lanes, list badges, and pill filters.
// Mirrors the shape used by ExpenseApprovalQueuePage so the two pages stay
// visually aligned.

const STATUSES = [
  { id: 'submitted', label: 'Inskickade', color: '#d97706', bg: '#fffbeb',                    icon: Send,           badgeClass: 'tr-badge-warn' },
  { id: 'approved',  label: 'Godkända',   color: '#059669', bg: 'rgba(5, 150, 105, 0.06)',    icon: Check,          badgeClass: 'tr-badge-ok'   },
  { id: 'rejected',  label: 'Avvisade',   color: '#dc2626', bg: 'rgba(239, 68, 68, 0.06)',    icon: AlertTriangle,  badgeClass: 'tr-badge-bad'  },
];
// Kept as an alias so existing kanban code reading `LANE_CONFIG` still works.
const LANE_CONFIG = STATUSES.map((s) => ({ key: s.id, label: s.label, color: s.color, bg: s.bg, icon: s.icon }));

const DRAG_TRANSITIONS = {
  submitted: ['approved', 'rejected'],
  approved: [],
  rejected: [],
};

const weekdayNamesShort = ['M', 'T', 'O', 'T', 'F', 'L', 'S'];

// ─── PERIOD CARD ───
//
// Memoized so a state change at the parent level (e.g. dragging another
// card, opening the search input) doesn't re-render every card in the
// kanban. With 50+ pending periods this matters a lot.
const PeriodCardImpl = ({ period, isDragging, isExpanded, onToggleExpand, entries, loadingEntries }) => {
  const name = period.firstname
    ? `${period.firstname} ${period.lastname || ''}`.trim()
    : null;
  const initials = name
    ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';
  const isDragDisabled = DRAG_TRANSITIONS[period.status]?.length === 0;

  // Content tags for at-a-glance visibility
  const contentTags = useMemo(() => {
    if (!entries?.length) return [];
    const tags = [];
    const seenLeave = new Set();
    const seenProject = new Set();
    for (const e of entries) {
      if (e.leave_type_id && !seenLeave.has(e.leave_type_id)) {
        seenLeave.add(e.leave_type_id);
        const icons = { SEM: '🏖️', SJUK: '🏥', PARENT: '👶', VAB: '🧒', COMP: '⏰' };
        tags.push({ label: e.leave_type_name || 'Frånvaro', color: '#d97706', icon: icons[e.leave_type_code] });
      }
      if (e.project_id && !e.leave_type_id && !seenProject.has(e.project_id)) {
        seenProject.add(e.project_id);
        tags.push({ label: e.project_name || 'Projekt', color: e.project_color || 'var(--primary)' });
      }
    }
    return tags;
  }, [entries]);

  // Group entries by project for the detail view
  const projectGroups = useMemo(() => {
    if (!entries?.length) return [];
    const groups = {};
    for (const e of entries) {
      const key = e.project_id || `leave-${e.leave_type_id}`;
      if (!groups[key]) {
        groups[key] = {
          name: e.project_name || e.leave_type_name || 'Övrigt',
          code: e.project_code || e.leave_type_code || '',
          color: e.project_color || (e.leave_type_id ? '#f59e0b' : '#6b7280'),
          isLeave: !!e.leave_type_id,
          entries: [],
          totalMinutes: 0,
        };
      }
      groups[key].entries.push(e);
      groups[key].totalMinutes += e.duration_minutes || 0;
    }
    return Object.values(groups).sort((a, b) => b.totalMinutes - a.totalMinutes);
  }, [entries]);

  // Build daily hours for the mini bar chart
  const dailyHours = useMemo(() => {
    if (!entries?.length) return [];
    try {
      const days = eachDayOfInterval({
        start: new Date(period.period_start),
        end: new Date(period.period_end),
      });
      return days.map(day => {
        const dayStr = format(day, 'yyyy-MM-dd');
        const mins = entries.filter(e => e.entry_date === dayStr).reduce((s, e) => s + (e.duration_minutes || 0), 0);
        return { date: day, hours: mins / 60, dayStr };
      });
    } catch (e) { return []; }
  }, [entries, period.period_start, period.period_end]);

  const maxDayHours = Math.max(...dailyHours.map(d => d.hours), 8);

  return (
    <div className={`approval-card${isDragging ? ' is-dragging' : ''}${isDragDisabled ? ' drag-disabled' : ''}${isExpanded ? ' is-expanded' : ''}`}>
      {/* Top row: avatar + name + drag handle */}
      <div className="d-flex align-items-start gap-2 mb-2">
        <div style={{
          width: 22, height: 22, borderRadius: 5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, background: 'var(--rgba-primary-1)', color: 'var(--primary)',
          fontSize: '0.5625rem', fontWeight: 700,
        }}>
          {initials}
        </div>
        <span style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--title)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {name || 'Okänd'}
        </span>
        {!isDragDisabled && <GripVertical size={14} className="drag-handle" />}
      </div>

      {/* Date range */}
      <div className="d-flex align-items-center gap-1 mb-2" style={{ fontSize: '0.75rem', color: 'var(--secondary)' }}>
        <Calendar size={11} style={{ opacity: 0.5 }} />
        <span>{format(new Date(period.period_start), 'd MMM')} – {format(new Date(period.period_end), 'd MMM yyyy')}</span>
      </div>

      {/* Hours + period type + expand toggle */}
      <div className="d-flex align-items-center justify-content-between">
        <span style={{ fontWeight: 700, fontSize: '0.8125rem', color: 'var(--title)' }}>
          {period.total_hours ? `${Math.round(Number(period.total_hours))}h` : '0h'}
        </span>
        <div className="d-flex align-items-center gap-2">
          <span style={{
            fontSize: '0.625rem', color: 'var(--secondary)', background: '#f3f4f6',
            padding: '1px 6px', borderRadius: 4,
          }}>
            {period.period_type === 'week' ? 'Vecka' : 'Månad'}
          </span>
          <button
            className="card-expand-btn"
            onClick={(e) => { e.stopPropagation(); onToggleExpand(period.period_id); }}
            title={isExpanded ? 'Dölj detaljer' : 'Visa detaljer'}
          >
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Content tags */}
      {contentTags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
          {contentTags.map((tag, i) => (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: '0.5625rem', fontWeight: 500, padding: '1px 6px',
              borderRadius: 4, background: `${tag.color}11`, color: tag.color,
              border: `1px solid ${tag.color}22`,
              whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {tag.icon && <span style={{ fontSize: '0.5625rem' }}>{tag.icon}</span>}
              {tag.label}
            </span>
          ))}
        </div>
      )}

      {/* Rejection reason */}
      {period.rejection_reason && (
        <div style={{
          marginTop: 8, padding: '6px 8px', borderRadius: 6,
          background: 'rgba(239, 68, 68, 0.06)', fontSize: '0.6875rem',
          color: '#dc2626', lineHeight: 1.3,
        }}>
          <AlertTriangle size={10} style={{ marginRight: 4, verticalAlign: -1 }} />
          {period.rejection_reason}
        </div>
      )}

      {/* Approved date */}
      {period.approved_at && (
        <div style={{ marginTop: 6, fontSize: '0.6875rem', color: 'var(--secondary)' }}>
          <Clock size={10} style={{ opacity: 0.4, marginRight: 3 }} />
          Godkänd {format(new Date(period.approved_at), 'd MMM yyyy')}
        </div>
      )}

      {/* ─── EXPANDED DETAIL VIEW ─── */}
      {isExpanded && (
        <div className="card-detail" onClick={(e) => e.stopPropagation()}>
          {loadingEntries ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <Spinner animation="border" size="sm" variant="secondary" />
            </div>
          ) : entries?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '12px 0', fontSize: '0.75rem', color: 'var(--secondary)' }}>
              Inga tidsregistreringar
            </div>
          ) : (
            <>
              {/* Mini daily bar chart */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: '0.625rem', fontWeight: 500, color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  Timmar per dag
                </div>
                <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 40 }}>
                  {dailyHours.map((d, i) => {
                    const barH = d.hours > 0 ? Math.max((d.hours / maxDayHours) * 36, 3) : 0;
                    const isOff = isWeekend(d.date);
                    const overwork = d.hours > 8;
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <div
                          style={{
                            width: '100%', maxWidth: 24, borderRadius: 3,
                            height: barH || 2,
                            background: d.hours === 0 ? '#e5e7eb'
                              : overwork ? '#dc2626'
                              : isOff ? '#f59e0b'
                              : 'var(--primary)',
                            opacity: d.hours === 0 ? 0.3 : 1,
                            transition: 'height 0.2s ease',
                          }}
                          title={`${format(d.date, 'd MMM')}: ${d.hours.toFixed(1)}h`}
                        />
                        <span style={{
                          fontSize: '0.5625rem', color: isOff ? '#d1d5db' : 'var(--secondary)',
                          fontWeight: d.hours > 0 ? 600 : 400, lineHeight: 1,
                        }}>
                          {weekdayNamesShort[i % 7]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Project breakdown */}
              <div style={{ fontSize: '0.625rem', fontWeight: 500, color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Fördelning
              </div>
              {projectGroups.map((group, i) => {
                const hours = (group.totalMinutes / 60);
                const pct = period.total_hours > 0 ? (hours / period.total_hours * 100) : 0;
                return (
                  <div key={i} className="detail-project-row">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <div style={{
                        width: 8, height: 8, borderRadius: 2, flexShrink: 0,
                        background: group.color,
                      }} />
                      <span style={{
                        fontSize: '0.75rem', fontWeight: 500, color: 'var(--title)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {group.name}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <div style={{ width: 48, height: 4, borderRadius: 2, background: '#f1f5f9', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: group.color, transition: 'width 0.3s ease' }} />
                      </div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--title)', minWidth: 28, textAlign: 'right' }}>
                        {hours.toFixed(hours % 1 ? 1 : 0)}h
                      </span>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const PeriodCard = React.memo(PeriodCardImpl, (prev, next) => (
  prev.period === next.period
  && prev.isDragging === next.isDragging
  && prev.isExpanded === next.isExpanded
  && prev.entries === next.entries
  && prev.loadingEntries === next.loadingEntries
  && prev.onToggleExpand === next.onToggleExpand
));

/* ─── LIST ROW — compact summary for list view ─── */
// `isOwn` (Phase 2.4) drives per-row F4 disable — when the caller's
// employeeId matches the period's employee_id, the Approve/Reject buttons
// are disabled with a tooltip. Server still rejects (assertCanApprovePeriod
// — pre-existing); this is the UX layer so admins on the *Alla* tab don't
// see "click → 403 toast on own row."
const PeriodListRow = ({ period, meta, entries, selected, selectable, onToggle, onApprove, onReject, isOwn }) => {
  const name = period.firstname
    ? `${period.firstname} ${period.lastname || ''}`.trim()
    : 'Okänd';
  const hours = period.total_hours ? Math.round(Number(period.total_hours)) : 0;
  const ownDisabledTitle = 'Du kan inte godkänna din egen tidrapport.';
  return (
    <div className="tr-list-row">
      <div className="tr-list-col-check" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          disabled={!selectable || isOwn}
          onChange={onToggle}
          title={isOwn ? ownDisabledTitle : undefined}
        />
      </div>
      <div className="tr-list-col-employee">{name}</div>
      <div className="tr-list-col-period">
        {format(new Date(period.period_start), 'd MMM')} – {format(new Date(period.period_end), 'd MMM yyyy')}
      </div>
      <div className="tr-list-col-type">
        <span className="tr-cat-chip">{period.period_type === 'week' ? 'Vecka' : 'Månad'}</span>
      </div>
      <div className="tr-list-col-hours">{hours}h</div>
      <div className="tr-list-col-status">
        {meta && <span className={`tr-badge ${meta.badgeClass}`}>{meta.label}</span>}
      </div>
      <div className="tr-list-col-actions">
        {period.status === 'submitted' && (
          <>
            <button
              className="tr-row-action tr-row-action-ok"
              onClick={onApprove}
              disabled={isOwn}
              title={isOwn ? ownDisabledTitle : 'Godkänn'}
            >
              <Check size={14} />
            </button>
            <button
              className="tr-row-action tr-row-action-bad"
              onClick={onReject}
              disabled={isOwn}
              title={isOwn ? ownDisabledTitle : 'Avvisa'}
            >
              <X size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───

// ─── SCOPE TABS (Phase 2.1/2.2/2.3) ───
//
// Each tab issues an explicit ?scope= request. Server enforces; the UI just
// drives the right query. §4.2 — expenses have no Mitt team tab; this page
// is time-reporting and DOES (per CAPABILITY_MATRIX.md §4 PM row).
//
// Visibility (driven from /me/capabilities):
//   - mine: capabilities.employeeId !== null
//   - team: capabilities.managesEmployeeIds.length > 0
//   - all : hasScope('time', 'view', 'all')
//
// Default tab: broadest available, mirroring how the user thinks about
// their primary surface. A solo founder lands on "Alla"; a manager who's
// also a regular employee lands on "Mitt team"; a regular employee lands
// on "Mina". The user can always switch.
const SCOPE_TABS = [
  { id: 'mine', label: 'Mina',      icon: User,      scope: 'mine' },
  { id: 'team', label: 'Mitt team', icon: Users,     scope: 'team' },
  { id: 'all',  label: 'Alla',      icon: Building2, scope: 'all'  },
];

const ApprovalPage = () => {
  const { companyId } = useParams();
  const { capabilities, hasScope, hasLoaded: capsLoaded } = useCapabilities();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // One bucket per tab now — the previous dual myPeriods/pendingPeriods
  // merged into an undifferentiated list (the brief's §2.3 "split it"
  // call-out). Tab refetches replace the bucket; no client-side merging.
  const [periods, setPeriods] = useState([]);
  const [activeTab, setActiveTab] = useState(null); // resolved after caps load
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [contentFilter, setContentFilter] = useState('all');
  const [dragSourceStatus, setDragSourceStatus] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [entryCache, setEntryCache] = useState({});
  const [allEntriesLoaded, setAllEntriesLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  // View mode — kanban default (drag-approval workflow), list for bulk review
  // of 50+ items. Persisted per browser. Matches the utlägg admin page.
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('timeApprovalViewMode') || 'kanban');
  const changeView = (m) => { setViewMode(m); try { localStorage.setItem('timeApprovalViewMode', m); } catch {} };
  // List view needs its own status pill filter (kanban already shows all lanes).
  // Defaults to 'all' so the list isn't empty when there are no submitted periods.
  const [listStatus, setListStatus] = useState('all');
  // Per-lane recency filter for terminal statuses (approved/rejected).
  // 0 = show all. Default 30 days mirrors the supplier-invoices kanban so
  // managers don't drown in a year of approved/rejected timesheets.
  const [approvedDays, setApprovedDays] = useState(30);
  const [rejectedDays, setRejectedDays] = useState(30);

  // ─── Tab visibility ───
  //
  // We compute these from the server's /me/capabilities snapshot, NEVER from
  // role names directly. capsLoaded gates so we don't render an "all hidden"
  // state during the initial fetch.
  const tabVisibility = useMemo(() => ({
    mine: Boolean(capabilities.employeeId),
    team: (capabilities.managesEmployeeIds || []).length > 0,
    all:  hasScope('time', 'view', 'all'),
  }), [capabilities, hasScope]);

  // Default to the broadest visible tab. A solo founder lands on Alla; a
  // pure manager on Mitt team; an employee on Mina. Resolves once caps
  // arrive — until then activeTab stays null and we don't fetch.
  useEffect(() => {
    if (!capsLoaded || activeTab !== null) return;
    if (tabVisibility.all)  setActiveTab('all');
    else if (tabVisibility.team) setActiveTab('team');
    else if (tabVisibility.mine) setActiveTab('mine');
  }, [capsLoaded, tabVisibility, activeTab]);

  const loadData = useCallback(async (isRefresh = false) => {
    if (!activeTab) return; // wait for caps + tab resolution
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      setError(null);
      // One scope-aware fetch per tab — server filters, FE never merges.
      // limit=200 is generous for the Alla tab (admin viewing tenant-wide
      // submitted+approved+rejected) and roomy for Mitt team teams up to
      // ~30 reports × ~6 recent periods. The kanban recency filter trims
      // the visible set further.
      const tabScope = SCOPE_TABS.find(t => t.id === activeTab)?.scope || 'mine';
      const res = await api.getTimesheetPeriods(companyId, { scope: tabScope, limit: 200 });
      const tabP = res.data.periods || [];
      setPeriods(tabP);

      // Batch-load entries for all periods in ONE round-trip via the
      // /entries/batch endpoint. Avoids the prior N+1 pattern.
      const validPeriods = tabP.filter(p =>
        ['submitted', 'approved', 'rejected'].includes(p.status),
      );
      const cache = {};
      if (validPeriods.length > 0) {
        try {
          const batchRes = await api.getTimeEntriesBatch(
            companyId,
            validPeriods.map(p => ({
              employeeId: p.employee_id,
              periodStart: p.period_start,
              periodEnd: p.period_end,
            })),
          );
          // The backend returns one item per request slot in matching order,
          // so we zip back to period_id by index.
          const items = batchRes.data?.results || [];
          validPeriods.forEach((p, i) => {
            cache[p.period_id] = items[i]?.entries || [];
          });
        } catch (e) {
          // Fall back to per-period fetch if the batch call itself fails
          // — keeps the page functional even if the batch route is missing
          // on an older backend or rejected by middleware.
          const fallback = await Promise.all(
            validPeriods.map(p =>
              api.getTimeEntries(companyId, {
                employeeId: p.employee_id,
                periodStart: p.period_start,
                periodEnd: p.period_end,
              })
                .then(r => ({ id: p.period_id, entries: r.data.entries || [] }))
                .catch(() => ({ id: p.period_id, entries: [] })),
            ),
          );
          fallback.forEach(r => { cache[r.id] = r.entries; });
        }
      }
      setEntryCache(cache);

      // Period.total_hours is no longer stored on the period row — compute it
      // from the entries we just fetched and attach it in-memory so downstream
      // components (PeriodCard, stats) keep working unchanged.
      const computeTotal = (entries) =>
        Math.round(entries.reduce((s, e) => s + (e.duration_minutes || 0), 0) / 60 * 100) / 100;
      setPeriods(prev => prev.map(p => ({ ...p, total_hours: computeTotal(cache[p.period_id] || []) })));

      setAllEntriesLoaded(true);
    } catch (err) {
      // F5 / scope-not-granted surfaces here as a 403 from the backend's
      // resolveTimeReportingScope. Render the message instead of a blank
      // page so the user understands why the tab is empty (UI Phase 2.2
      // hides empty-team tabs upstream, but defense in depth).
      setError(err.response?.data?.message || 'Kunde inte ladda data');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [companyId, activeTab]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggleExpand = useCallback((periodId) => {
    setExpandedId(prev => prev === periodId ? null : periodId);
  }, []);

  // Single tab-scoped bucket; no merging needed (Phase 2.3 split).
  const allPeriods = periods;

  // Build filter options from all entries
  const filterOptions = useMemo(() => {
    const projects = new Map();
    const leaveTypes = new Map();
    for (const entries of Object.values(entryCache)) {
      for (const e of entries) {
        if (e.project_id && e.project_name) projects.set(e.project_id, e.project_name);
        if (e.leave_type_id && e.leave_type_name) leaveTypes.set(e.leave_type_id, e.leave_type_name);
      }
    }
    return {
      projects: Array.from(projects, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
      leaveTypes: Array.from(leaveTypes, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [entryCache]);

  const filteredPeriods = useMemo(() => {
    let items = allPeriods;

    // Period type filter
    if (periodFilter === 'week') items = items.filter(p => p.period_type === 'week');
    if (periodFilter === 'month') items = items.filter(p => p.period_type === 'month');

    // Content filter
    if (contentFilter !== 'all' && allEntriesLoaded) {
      items = items.filter(p => {
        const entries = entryCache[p.period_id] || [];
        if (contentFilter === 'work') return entries.some(e => e.project_id && !e.leave_type_id);
        if (contentFilter === 'leave') return entries.some(e => e.leave_type_id);
        if (contentFilter.startsWith('project:')) return entries.some(e => e.project_id === contentFilter.slice(8));
        if (contentFilter.startsWith('leave:')) return entries.some(e => e.leave_type_id === contentFilter.slice(6));
        return true;
      });
    }

    // Text search — match employee name, date, project, leave type
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(p => {
        if (p.firstname && `${p.firstname} ${p.lastname || ''}`.toLowerCase().includes(q)) return true;
        if (p.period_start?.includes(q) || p.period_end?.includes(q)) return true;
        if (allEntriesLoaded) {
          const entries = entryCache[p.period_id] || [];
          return entries.some(e =>
            (e.project_name && e.project_name.toLowerCase().includes(q)) ||
            (e.project_code && e.project_code.toLowerCase().includes(q)) ||
            (e.leave_type_name && e.leave_type_name.toLowerCase().includes(q)) ||
            (e.description && e.description.toLowerCase().includes(q))
          );
        }
        return false;
      });
    }

    return items;
  }, [allPeriods, searchTerm, periodFilter, contentFilter, entryCache, allEntriesLoaded]);

  const { groupedPeriods, laneTotals } = useMemo(() => {
    const grouped = { submitted: [], approved: [], rejected: [] };
    for (const p of filteredPeriods) {
      if (grouped[p.status]) grouped[p.status].push(p);
    }
    for (const key of Object.keys(grouped)) {
      grouped[key].sort((a, b) => b.period_start.localeCompare(a.period_start));
    }
    // Snapshot pre-recency-filter counts so the lane header can show
    // "12 av 84" when a recency window is hiding older items.
    const totals = {
      submitted: grouped.submitted.length,
      approved: grouped.approved.length,
      rejected: grouped.rejected.length,
    };
    // Recency filter: approved → approved_at, rejected → updated_at
    // (no rejected_at column in schema; updated_at is set by the reject
    // path so it's the right proxy for "when this was rejected").
    const cutoffFromDays = (days) => {
      const c = new Date();
      c.setDate(c.getDate() - days);
      return c;
    };
    if (approvedDays > 0) {
      const cutoff = cutoffFromDays(approvedDays);
      grouped.approved = grouped.approved.filter(p =>
        p.approved_at && new Date(p.approved_at) >= cutoff,
      );
    }
    if (rejectedDays > 0) {
      const cutoff = cutoffFromDays(rejectedDays);
      grouped.rejected = grouped.rejected.filter(p =>
        p.updated_at && new Date(p.updated_at) >= cutoff,
      );
    }
    return { groupedPeriods: grouped, laneTotals: totals };
  }, [filteredPeriods, approvedDays, rejectedDays]);

  const stats = useMemo(() => ({
    submitted: allPeriods.filter(p => p.status === 'submitted').length,
    approved: allPeriods.filter(p => p.status === 'approved').length,
    rejected: allPeriods.filter(p => p.status === 'rejected').length,
    totalHours: allPeriods.filter(p => p.status === 'approved').reduce((s, p) => s + (Number(p.total_hours) || 0), 0),
  }), [allPeriods]);

  const isValidDropTarget = (laneKey) => {
    if (!dragSourceStatus) return true;
    return DRAG_TRANSITIONS[dragSourceStatus]?.includes(laneKey) || false;
  };

  const handleDragStart = (start) => {
    setExpandedId(null); // Collapse any expanded card when dragging starts
    const period = allPeriods.find(p => p.period_id === start.draggableId);
    if (period) setDragSourceStatus(period.status);
  };

  const handleDragEnd = (result) => {
    setDragSourceStatus(null);
    const { destination, draggableId } = result;
    if (!destination) return;

    const fromStatus = result.source.droppableId;
    const toStatus = destination.droppableId;
    if (fromStatus === toStatus) return;

    if (!DRAG_TRANSITIONS[fromStatus]?.includes(toStatus)) return;

    if (toStatus === 'approved') {
      handleApprove(draggableId);
    } else if (toStatus === 'rejected') {
      setRejectingId(draggableId);
    }
  };

  const handleApprove = async (periodId) => {
    try {
      setProcessing(true);
      await api.approvePeriod(companyId, periodId);
      setExpandedId(null);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte godkänna');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (periodId) => {
    if (!rejectReason.trim()) return;
    try {
      setProcessing(true);
      await api.rejectPeriod(companyId, periodId, rejectReason);
      setRejectingId(null);
      setRejectReason('');
      setExpandedId(null);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte avvisa');
    } finally {
      setProcessing(false);
    }
  };

  // ─── BULK ACTIONS ───

  const toggleSelectId = (periodId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(periodId) ? next.delete(periodId) : next.add(periodId);
      return next;
    });
  };

  // Bulk approve only operates on submitted-status periods AND excludes the
  // caller's own rows (Phase 2.4 F4 guard): a solo founder on the Alla tab
  // who clicks "Välj alla väntande" must not bulk-approve their own. Per-
  // row buttons also disabled with a tooltip below.
  const ownEmployeeId = capabilities.employeeId;
  const submittedPeriodIds = useMemo(
    () => periods
      .filter(p => p.status === 'submitted' && p.employee_id !== ownEmployeeId)
      .map(p => p.period_id),
    [periods, ownEmployeeId],
  );

  const allSubmittedSelected = submittedPeriodIds.length > 0
    && submittedPeriodIds.every(id => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (allSubmittedSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(submittedPeriodIds));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    try {
      setProcessing(true);
      // Process sequentially so partial failures don't leave inconsistent state
      const results = await Promise.allSettled(
        [...selectedIds].map((id) => api.approvePeriod(companyId, id)),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        setError(`${failed} av ${selectedIds.size} kunde inte godkännas. Försök igen.`);
      }
      setSelectedIds(new Set());
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte godkänna');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.size === 0 || !bulkRejectReason.trim()) return;
    try {
      setProcessing(true);
      const results = await Promise.allSettled(
        [...selectedIds].map((id) => api.rejectPeriod(companyId, id, bulkRejectReason)),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        setError(`${failed} av ${selectedIds.size} kunde inte avvisas.`);
      }
      setSelectedIds(new Set());
      setBulkRejectOpen(false);
      setBulkRejectReason('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte avvisa');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const totalCount = filteredPeriods.filter(p => ['submitted', 'approved', 'rejected'].includes(p.status)).length;

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; }

        /* Stats Grid */
        .approval-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1.25rem;
        }
        .approval-stat-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }
        .approval-stat-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }
        .approval-stat-icon {
          width: 48px; height: 48px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 1rem;
        }
        .approval-stat-value { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.375rem; }
        .approval-stat-label { color: var(--secondary); font-size: 0.75rem; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.25rem; }
        .approval-stat-subtitle { color: var(--secondary); font-size: 0.6875rem; }

        /* Kanban Board */
        .approval-board {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          overflow-x: auto;
          padding-bottom: 1rem;
        }
        .approval-lane {
          flex: 1;
          min-width: 0;
          background: #f8f9fb;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          max-height: calc(100vh - 340px);
          min-height: 400px;
          transition: all 0.2s ease;
        }
        .approval-lane.lane-empty {
          background: transparent;
          border: 2px dashed #b0b8c4;
          min-height: 200px;
          opacity: 0.6;
        }
        .approval-lane.lane-empty .lane-header {
          background: transparent;
        }
        .lane-empty-placeholder {
          text-align: center;
          padding: 1.5rem 0.75rem;
          color: var(--secondary);
          font-size: 0.75rem;
          opacity: 0.5;
        }
        .approval-lane.lane-drag-over {
          background: rgba(59, 130, 246, 0.06);
          box-shadow: inset 0 0 0 2px var(--primary);
          border-radius: 12px;
        }
        .approval-lane.lane-drop-invalid {
          opacity: 0.3;
          filter: grayscale(0.5);
        }
        .lane-header {
          padding: 0.875rem 1rem;
          background: white;
          border-radius: 12px 12px 0 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .lane-date-filter {
          display: block;
          width: 100%;
          margin-top: 0.5rem;
          padding: 0.25rem 0.375rem;
          font-size: 0.6875rem;
          color: var(--secondary);
          background: #f8f9fb;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          outline: none;
        }
        .lane-date-filter:focus {
          border-color: var(--primary);
        }
        .lane-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0 0.5rem;
        }
        .lane-cards {
          padding: 0.5rem;
          overflow-y: auto;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .lane-cards::-webkit-scrollbar { width: 4px; }
        .lane-cards::-webkit-scrollbar-track { background: transparent; }
        .lane-cards::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }

        /* Period Card */
        .approval-card {
          background: white;
          border: 1px solid #e8ecf1;
          border-radius: 8px;
          padding: 0.75rem 0.875rem;
          cursor: grab;
          transition: all 0.15s ease;
          user-select: none;
        }
        .approval-card:hover {
          border-color: var(--primary);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }
        .approval-card.is-dragging {
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
          border-color: var(--primary);
          opacity: 0.95;
        }
        .approval-card.drag-disabled {
          cursor: default;
        }
        .approval-card.is-expanded {
          border-color: var(--primary);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }
        .drag-handle {
          color: #d1d5db;
          opacity: 0;
          transition: opacity 0.15s ease;
          flex-shrink: 0;
        }
        .approval-card:hover .drag-handle {
          opacity: 1;
        }

        /* Card expand button */
        .card-expand-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px; height: 22px;
          border-radius: 4px;
          border: none;
          background: #f3f4f6;
          color: var(--secondary);
          cursor: pointer;
          transition: all 0.15s ease;
          padding: 0;
        }
        .card-expand-btn:hover {
          background: var(--rgba-primary-1);
          color: var(--primary);
        }

        /* Card detail section */
        .card-detail {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #f1f5f9;
        }
        .detail-project-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 4px 0;
          gap: 8px;
        }

        /* Search input */
        .board-search {
          position: relative;
        }
        .board-search input {
          padding-left: 2.25rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.875rem;
          height: 38px;
          width: 280px;
          transition: all 0.2s ease;
          background: white;
        }
        .board-search input:focus {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
          outline: none;
        }
        .board-search .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--secondary);
          opacity: 0.5;
        }

        @media (max-width: 1024px) {
          .approval-stats-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 768px) {
          .approval-stats-grid { grid-template-columns: 1fr 1fr; }
        }

        /* ─── NEW: thin stats strip + unified toolbar + pills + list view ─── */
        .tr-stats-strip {
          display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
          padding: 8px 14px; margin-bottom: 12px;
          background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 8px;
          font-size: 0.8125rem; color: #475569;
        }
        .tr-stat b { font-variant-numeric: tabular-nums; margin-right: 4px; }
        .tr-stat-sep { color: #cbd5e1; }

        .tr-toolbar {
          display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
          padding: 10px 14px; background: white;
          border: 1px solid #e5e7eb; border-radius: 10px;
        }
        .tr-search { position: relative; flex: 1; min-width: 240px; max-width: 420px; }
        .tr-search-icon {
          position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
          color: #9ca3af;
        }
        .tr-search-input {
          width: 100%; height: 36px; padding: 0 12px 0 34px;
          border: 1px solid #e5e7eb; border-radius: 8px;
          font-size: 0.875rem; background: #f9fafb;
          transition: all 0.15s;
        }
        .tr-search-input:focus {
          outline: none; background: white;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
        }
        .tr-toolbar-right { display: flex; align-items: center; gap: 8px; margin-left: auto; }
        .tr-view-toggle {
          display: flex; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;
        }
        .tr-view-btn {
          width: 34px; height: 34px; border: none; background: white;
          display: flex; align-items: center; justify-content: center;
          color: #6b7280; cursor: pointer; transition: all 0.15s;
        }
        .tr-view-btn.active { background: var(--rgba-primary-1); color: var(--bs-primary); }
        .tr-view-btn:hover:not(.active) { background: #f3f4f6; }
        .tr-btn-icon {
          width: 34px; height: 34px; border: 1px solid #e5e7eb; border-radius: 8px;
          background: white; color: #6b7280; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s;
        }
        .tr-btn-icon:hover { background: #f3f4f6; color: #111827; }
        .tr-btn-icon:disabled { opacity: 0.5; cursor: not-allowed; }

        .tr-pills {
          display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px;
        }
        .tr-pills-compact { margin-bottom: 14px; }
        .tr-pill {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 12px; border: 1px solid #d1d5db; background: white;
          border-radius: 999px; font-size: 0.8125rem; font-weight: 500;
          color: #111827; cursor: pointer; transition: all 0.15s;
        }
        .tr-pill:hover { background: #f3f4f6; }
        .tr-pill.active {
          background: var(--rgba-primary-2); color: var(--bs-primary);
          border-color: var(--bs-primary); font-weight: 600;
        }
        .tr-pill-count {
          font-size: 0.6875rem; padding: 1px 6px; border-radius: 999px;
          background: #e5e7eb; color: #374151; font-weight: 600;
        }
        .tr-pill.active .tr-pill-count {
          background: var(--bs-primary); color: white;
        }

        /* Badges */
        .tr-badge {
          display: inline-block; padding: 2px 8px; border-radius: 999px;
          font-size: 0.625rem; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.02em;
          white-space: nowrap;
        }
        .tr-badge-warn { background: #fef3c7; color: #92400e; }
        .tr-badge-ok   { background: #d1fae5; color: #065f46; }
        .tr-badge-bad  { background: #fee2e2; color: #991b1b; }

        /* List view */
        .tr-list {
          background: white; border: 1px solid #e5e7eb; border-radius: 10px;
          overflow: hidden;
        }
        .tr-list-header, .tr-list-row {
          display: grid;
          grid-template-columns: 28px 1fr 200px 90px 80px 130px 80px;
          gap: 12px; padding: 10px 14px; align-items: center;
          font-size: 0.8125rem;
        }
        .tr-list-header {
          background: #f8fafc; border-bottom: 1px solid #e5e7eb;
          font-size: 0.6875rem; font-weight: 600; color: #64748b;
          text-transform: uppercase; letter-spacing: 0.04em;
        }
        .tr-list-row { border-bottom: 1px solid #f1f5f9; transition: background-color 0.15s; }
        .tr-list-row:hover { background: #f8fafc; }
        .tr-list-row:last-child { border-bottom: none; }
        .tr-list-col-employee { color: #0f172a; font-weight: 600; }
        .tr-list-col-hours { text-align: right; font-weight: 600; font-variant-numeric: tabular-nums; }
        .tr-list-col-actions { display: flex; gap: 4px; justify-content: flex-end; }
        .tr-cat-chip {
          display: inline-block; padding: 2px 8px; border-radius: 6px;
          background: #f3f4f6; color: #374151; font-size: 0.6875rem;
        }
        .tr-row-action {
          width: 28px; height: 28px; border-radius: 6px;
          background: white; border: 1px solid #e5e7eb; color: #6b7280;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s;
        }
        .tr-row-action-ok:hover  { background: #d1fae5; border-color: #10b981; color: #065f46; }
        .tr-row-action-bad:hover { background: #fee2e2; border-color: #ef4444; color: #991b1b; }

        .tr-empty {
          text-align: center; padding: 48px 16px; color: #9ca3af;
          background: white; border: 1px dashed #e5e7eb; border-radius: 10px;
        }
      `}</style>

      <PageTitle activeMenu="Hantera tidrapporter" motherMenu="Tid" />
      <div className="col-12">
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {/* ─── Phase 2.1/2.2/2.3 — Scope tabs ─── */}
        {/* Hidden tabs are intentionally not rendered (vs. disabled): we
            DON'T want to advertise scopes the caller can't access. The
            ?scope= request is the explicit gatekeeper; sidebar gating
            (Phase 2.0) and CapabilityRoute (Phase 2.5) are the layered
            defenses. */}
        {capsLoaded && (() => {
          const visibleTabs = SCOPE_TABS.filter(t => tabVisibility[t.id]);
          if (visibleTabs.length === 0) {
            // Caller is on this page despite having no scope — likely
            // bookmarked URL hit before CapabilityRoute mount lands.
            // Show a clear empty-state rather than an empty kanban.
            return (
              <div className="tr-empty" style={{ marginBottom: 12 }}>
                Du saknar behörighet att se några tidrapporter här.
              </div>
            );
          }
          // Single-tab case (e.g., manager who's not also an admin and not
          // a regular employee with their own report — mostly hypothetical
          // with seeded data) — render no tab strip; just go straight to
          // the content. Avoids a nonsense one-tab-pill row.
          if (visibleTabs.length === 1) return null;
          return (
            <div className="tr-pills tr-pills-compact" role="tablist" aria-label="Omfång">
              {visibleTabs.map(t => {
                const TabIcon = t.icon;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === t.id}
                    className={`tr-pill ${activeTab === t.id ? 'active' : ''}`}
                    onClick={() => {
                      // Reset transient selection state when switching scope —
                      // a row selected in *Mitt team* makes no sense in *Alla*.
                      setActiveTab(t.id);
                      setSelectedIds(new Set());
                      setExpandedId(null);
                    }}
                  >
                    <TabIcon size={13} style={{ marginRight: 4 }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          );
        })()}

        {/* Thin stats strip — single-line summary (lane counts already show
            these in the kanban; this is just a header glance). */}
        <div className="tr-stats-strip">
          <span className="tr-stat"><b style={{ color: '#d97706' }}>{stats.submitted}</b> väntar</span>
          <span className="tr-stat-sep">·</span>
          <span className="tr-stat"><b style={{ color: '#059669' }}>{stats.approved}</b> godkända</span>
          <span className="tr-stat-sep">·</span>
          <span className="tr-stat"><b style={{ color: '#dc2626' }}>{stats.rejected}</b> avvisade</span>
          <span className="tr-stat-sep">·</span>
          <span className="tr-stat">{stats.totalHours}h godkänt totalt</span>
        </div>

        {/* Unified toolbar — matches Hantera utlägg pattern. */}
        <div className="tr-toolbar">
          <div className="tr-search">
            <Search size={14} className="tr-search-icon" />
            <input
              className="tr-search-input"
              placeholder="Sök anställd, projekt, frånvaro…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="tr-toolbar-right">
            <div className="tr-view-toggle" title="Vy">
              <button
                className={`tr-view-btn ${viewMode === 'kanban' ? 'active' : ''}`}
                onClick={() => changeView('kanban')}
                aria-label="Kanban-vy"
              >
                <LayoutGrid size={14} />
              </button>
              <button
                className={`tr-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => changeView('list')}
                aria-label="Listvy"
              >
                <ListIcon size={14} />
              </button>
            </div>
            <button
              className="tr-btn-icon"
              onClick={() => loadData(true)}
              disabled={refreshing}
              title="Uppdatera"
              aria-label="Uppdatera"
            >
              <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            </button>
          </div>
        </div>

        {/* Content pill row — Arbete / Frånvaro, then dynamic projekt + leave-type chips */}
        <div className="tr-pills">
          <button className={`tr-pill ${contentFilter === 'all' ? 'active' : ''}`} onClick={() => setContentFilter('all')}>
            Allt innehåll
          </button>
          <button className={`tr-pill ${contentFilter === 'work' ? 'active' : ''}`} onClick={() => setContentFilter('work')}>
            Arbete
          </button>
          <button className={`tr-pill ${contentFilter === 'leave' ? 'active' : ''}`} onClick={() => setContentFilter('leave')}>
            Frånvaro
          </button>
          {filterOptions.leaveTypes.map((lt) => (
            <button
              key={`lt-${lt.id}`}
              className={`tr-pill ${contentFilter === `leave:${lt.id}` ? 'active' : ''}`}
              onClick={() => setContentFilter(`leave:${lt.id}`)}
            >
              {lt.name}
            </button>
          ))}
          {filterOptions.projects.map((p) => (
            <button
              key={`pr-${p.id}`}
              className={`tr-pill ${contentFilter === `project:${p.id}` ? 'active' : ''}`}
              onClick={() => setContentFilter(`project:${p.id}`)}
            >
              {p.name}
            </button>
          ))}
        </div>

        {/* Period type pill row */}
        <div className="tr-pills tr-pills-compact">
          <button className={`tr-pill ${periodFilter === 'all' ? 'active' : ''}`} onClick={() => setPeriodFilter('all')}>
            Alla perioder <span className="tr-pill-count">{totalCount}</span>
          </button>
          <button className={`tr-pill ${periodFilter === 'week' ? 'active' : ''}`} onClick={() => setPeriodFilter('week')}>
            Veckor
          </button>
          <button className={`tr-pill ${periodFilter === 'month' ? 'active' : ''}`} onClick={() => setPeriodFilter('month')}>
            Månader
          </button>
        </div>

        {/* Status pill row — only relevant for list view (kanban shows all statuses as lanes). */}
        {viewMode === 'list' && (
          <div className="tr-pills tr-pills-compact">
            <button
              className={`tr-pill ${listStatus === 'all' ? 'active' : ''}`}
              onClick={() => setListStatus('all')}
            >
              Alla
              <span className="tr-pill-count">{filteredPeriods.length}</span>
            </button>
            {STATUSES.map((s) => {
              const n = filteredPeriods.filter((p) => p.status === s.id).length;
              return (
                <button
                  key={s.id}
                  className={`tr-pill ${listStatus === s.id ? 'active' : ''}`}
                  onClick={() => setListStatus(s.id)}
                >
                  {s.label}
                  {n > 0 && <span className="tr-pill-count">{n}</span>}
                </button>
              );
            })}
          </div>
        )}

        {refreshing && (
          <div className="text-center mb-3" style={{ fontSize: '0.8125rem', color: 'var(--secondary)' }}>
            <RefreshCw size={14} className="me-1 spin" />
            Uppdaterar...
          </div>
        )}

        {/* Bulk Action Bar — appears when items are selected */}
        {submittedPeriodIds.length > 0 && (
          <div
            className="d-flex align-items-center gap-2 mb-3"
            style={{
              padding: '12px 16px',
              background: selectedIds.size > 0 ? 'rgba(14, 165, 233, 0.06)' : '#f8fafc',
              border: `1px solid ${selectedIds.size > 0 ? '#0ea5e9' : '#e2e8f0'}`,
              borderRadius: 8,
              transition: 'all 150ms ease',
            }}
          >
            <input
              type="checkbox"
              checked={allSubmittedSelected}
              onChange={toggleSelectAll}
              aria-label="Välj alla väntande tidrapporter"
              id="bulk-select-all"
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="bulk-select-all" style={{ fontSize: 13, fontWeight: 500, marginBottom: 0, cursor: 'pointer' }}>
              {selectedIds.size > 0
                ? `${selectedIds.size} av ${submittedPeriodIds.length} valda`
                : `Välj alla väntande (${submittedPeriodIds.length})`}
            </label>
            {selectedIds.size > 0 && (
              <div className="ms-auto d-flex gap-2">
                <Button
                  size="sm"
                  variant="success"
                  onClick={handleBulkApprove}
                  disabled={processing}
                >
                  <Check size={14} className="me-1" />
                  Godkänn {selectedIds.size}
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => setBulkRejectOpen(true)}
                  disabled={processing}
                >
                  <X size={14} className="me-1" />
                  Avvisa {selectedIds.size}
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => setSelectedIds(new Set())}
                  style={{ color: 'var(--secondary)', textDecoration: 'none' }}
                >
                  Avmarkera
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Bulk Reject Modal */}
        <Modal show={bulkRejectOpen} onHide={() => { setBulkRejectOpen(false); setBulkRejectReason(''); }} centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 16 }}>Avvisa {selectedIds.size} tidrapporter</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
              Samma anledning skickas till alla anställda. De får en notis om avvisningen.
            </p>
            <textarea
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              placeholder="Anledning till avvisning..."
              rows={4}
              style={{ width: '100%', padding: 10, border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
              autoFocus
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => { setBulkRejectOpen(false); setBulkRejectReason(''); }}>
              Avbryt
            </Button>
            <Button variant="danger" onClick={handleBulkReject} disabled={!bulkRejectReason.trim() || processing}>
              Avvisa {selectedIds.size} tidrapporter
            </Button>
          </Modal.Footer>
        </Modal>

        {/* LIST VIEW — simpler surface for 50+ items, defaults to "Alla"
            but pill bar above switches. Row-level bulk-select + approve. */}
        {viewMode === 'list' && (() => {
          const rows = filteredPeriods
            .filter((p) => listStatus === 'all' ? true : p.status === listStatus)
            .sort((a, b) => b.period_start.localeCompare(a.period_start));
          if (rows.length === 0) {
            return <div className="tr-empty">Inga tidrapporter matchar filtret.</div>;
          }
          const submittedRows = rows.filter((p) => p.status === 'submitted');
          const allSubmittedRowsSelected = submittedRows.length > 0
            && submittedRows.every((p) => selectedIds.has(p.period_id));
          return (
            <div className="tr-list">
              <div className="tr-list-header">
                <div className="tr-list-col-check">
                  <input
                    type="checkbox"
                    checked={allSubmittedRowsSelected}
                    onChange={() => {
                      if (submittedRows.length === 0) return;
                      setSelectedIds(allSubmittedRowsSelected
                        ? new Set()
                        : new Set(submittedRows.map((p) => p.period_id)));
                    }}
                    disabled={submittedRows.length === 0}
                  />
                </div>
                <div className="tr-list-col-employee">Anställd</div>
                <div className="tr-list-col-period">Period</div>
                <div className="tr-list-col-type">Typ</div>
                <div className="tr-list-col-hours">Timmar</div>
                <div className="tr-list-col-status">Status</div>
                <div className="tr-list-col-actions" />
              </div>
              {rows.map((p) => (
                <PeriodListRow
                  key={p.period_id}
                  period={p}
                  meta={STATUSES.find((s) => s.id === p.status)}
                  entries={entryCache[p.period_id] || []}
                  selected={selectedIds.has(p.period_id)}
                  onToggle={() => toggleSelectId(p.period_id)}
                  selectable={p.status === 'submitted'}
                  onApprove={() => handleApprove(p.period_id)}
                  onReject={() => setRejectingId(p.period_id)}
                  isOwn={!!capabilities.employeeId && p.employee_id === capabilities.employeeId}
                />
              ))}
            </div>
          );
        })()}

        {/* KANBAN VIEW */}
        {viewMode === 'kanban' && (
        <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="approval-board">
            {LANE_CONFIG.filter(lane => {
              // Hide the Avvisade lane when empty, unless a card is being
              // dragged from Inskickade — in which case we need the drop
              // target to be reachable. Approved/Submitted always render.
              if (lane.key !== 'rejected') return true;
              if ((groupedPeriods.rejected || []).length > 0) return true;
              return dragSourceStatus === 'submitted';
            }).map(lane => {
              const items = groupedPeriods[lane.key] || [];
              const isEmpty = items.length === 0;
              const isValidDrop = isValidDropTarget(lane.key);
              const LaneIcon = lane.icon;

              return (
                <Droppable
                  droppableId={lane.key}
                  key={lane.key}
                  isDropDisabled={!isValidDrop}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={[
                        'approval-lane',
                        isEmpty && !snapshot.isDraggingOver ? 'lane-empty' : '',
                        snapshot.isDraggingOver ? 'lane-drag-over' : '',
                        dragSourceStatus && !isValidDrop ? 'lane-drop-invalid' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div className="lane-header">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-2">
                            <LaneIcon size={14} style={{ color: isEmpty && !snapshot.isDraggingOver ? 'var(--secondary)' : lane.color }} />
                            <span style={{ fontWeight: 600, fontSize: '0.8125rem', color: isEmpty && !snapshot.isDraggingOver ? 'var(--secondary)' : 'var(--title)' }}>
                              {lane.label}
                            </span>
                          </div>
                          {(!isEmpty || snapshot.isDraggingOver) && (() => {
                            const isTerminal = lane.key === 'approved' || lane.key === 'rejected';
                            const days = lane.key === 'approved' ? approvedDays : rejectedDays;
                            const total = laneTotals[lane.key] || 0;
                            const showRatio = isTerminal && days > 0 && total > items.length;
                            return (
                              <span className="lane-count" style={{ background: lane.bg, color: lane.color }}>
                                {showRatio ? `${items.length} av ${total}` : items.length}
                              </span>
                            );
                          })()}
                        </div>
                        {(lane.key === 'approved' || lane.key === 'rejected') && (
                          <select
                            className="lane-date-filter"
                            value={lane.key === 'approved' ? approvedDays : rejectedDays}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              if (lane.key === 'approved') setApprovedDays(v);
                              else setRejectedDays(v);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value={7}>Senaste 7 dagar</option>
                            <option value={30}>Senaste 30 dagar</option>
                            <option value={90}>Senaste 90 dagar</option>
                            <option value={0}>Visa alla</option>
                          </select>
                        )}
                      </div>

                      <div className="lane-cards">
                        {isEmpty && !snapshot.isDraggingOver ? (
                          <div className="lane-empty-placeholder">
                            {dragSourceStatus && isValidDrop
                              ? 'Släpp här'
                              : lane.key === 'submitted' ? 'Inga inskickade tidrapporter'
                                : lane.key === 'approved' ? 'Inga godkända tidrapporter'
                                  : 'Inga avvisade tidrapporter'}
                          </div>
                        ) : (
                          items.map((period, index) => {
                            // Phase 2.4 F4: disable drag (which auto-approves
                            // on drop into Godkända) when the row is the
                            // caller's own. Per-row checkbox + bulk-approve
                            // are independently filtered above.
                            const isOwn = !!ownEmployeeId && period.employee_id === ownEmployeeId;
                            const isDragDisabled = processing
                              || DRAG_TRANSITIONS[lane.key]?.length === 0
                              || isOwn;
                            return (
                              <Draggable
                                key={period.period_id}
                                draggableId={period.period_id}
                                index={index}
                                isDragDisabled={isDragDisabled}
                              >
                                {(provided, snapshot) => {
                                  const isSubmitted = period.status === 'submitted';
                                  const isSelected = selectedIds.has(period.period_id);
                                  const child = (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={{
                                        ...provided.draggableProps.style,
                                        position: 'relative',
                                      }}
                                      title={isOwn ? 'Du kan inte godkänna din egen tidrapport.' : undefined}
                                    >
                                      {isSubmitted && !snapshot.isDragging && (
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          disabled={isOwn}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={() => toggleSelectId(period.period_id)}
                                          aria-label={`Välj ${period.firstname} ${period.lastname}`}
                                          title={isOwn ? 'Du kan inte godkänna din egen tidrapport.' : undefined}
                                          style={{
                                            position: 'absolute',
                                            top: 10,
                                            left: 10,
                                            zIndex: 2,
                                            cursor: isOwn ? 'not-allowed' : 'pointer',
                                            width: 16,
                                            height: 16,
                                          }}
                                        />
                                      )}
                                      <div style={isSubmitted ? { paddingLeft: 22 } : undefined}>
                                        <PeriodCard
                                          period={period}
                                          isDragging={snapshot.isDragging}
                                          isExpanded={expandedId === period.period_id && !snapshot.isDragging}
                                          onToggleExpand={handleToggleExpand}
                                          entries={entryCache[period.period_id]}
                                          loadingEntries={false}
                                        />
                                      </div>
                                    </div>
                                  );
                                  if (snapshot.isDragging) {
                                    return ReactDOM.createPortal(child, document.body);
                                  }
                                  return child;
                                }}
                              </Draggable>
                            );
                          })
                        )}
                        {provided.placeholder}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
        )}
      </div>

      {/* Reject modal */}
      <Modal show={!!rejectingId} onHide={() => { setRejectingId(null); setRejectReason(''); }}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title className="fs-5" style={{ fontWeight: 700 }}>Avvisa tidrapport</Modal.Title>
            <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)', marginTop: 4 }}>
              Ange en anledning så att den anställde vet vad som behöver justeras
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label style={{ fontSize: '0.8125rem', fontWeight: 500 }}>Anledning</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="T.ex. saknade projektuppgifter för onsdag..."
              autoFocus
              style={{ borderRadius: 8, fontSize: '0.875rem' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setRejectingId(null); setRejectReason(''); }}
            style={{ borderRadius: 8, fontSize: '0.8125rem' }}
          >
            Avbryt
          </Button>
          <Button variant="danger"
            onClick={() => handleReject(rejectingId)}
            disabled={!rejectReason.trim() || processing}
            className="d-flex align-items-center gap-1"
            style={{ borderRadius: 8, fontSize: '0.8125rem' }}
          >
            <X size={14} /> Avvisa
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ApprovalPage;
